import { useState } from 'react';

import { useRentalManagerUserExperimentWebApiHandler_getTreatment } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

import {
  getCookieAsBoolean,
  LISTING_CENTRIC_VIEW_SPLIT_FEATURE_NAME,
  ZRM_IA_NAV_ENABLED,
  ZRM_LISTING_LIST_ENABLED,
} from '../utils/featureFlags';

export type UseListingCentricViewEnabledParams = {
  loggedIn?: boolean;
  /** Returns this value verbatim and skips both the cookie read and the Split call. */
  override?: boolean;
};

/**
 * Test B (ZRM-16134) gate, shared by the Listings entry point and the IA top nav bar - they
 * ship together, so one treatment drives both. React Query dedupes on the shared key, so
 * calling this from several nav surfaces costs one request.
 *
 * Uses the generated ts-client hook rather than `useGetTreatment` from `@zg-rentals/split`
 * because the latter requires `SplitProvider`, which most apps consuming this module do not
 * mount. `isLoading` is for route guards - redirecting before the treatment resolves would
 * bounce every enrolled user.
 */
export const useListingCentricViewEnabled = ({ loggedIn, override }: UseListingCentricViewEnabledParams = {}) => {
  // Read after mount, not during render: the cookie is false on the server and would mismatch
  // on hydration. Layout effect so QA opt-ins apply before paint, with no chrome shift - only
  // the treatment below has to resolve over the network.
  const [cookie, setCookie] = useState({ checked: false, enabled: false });

  useIsomorphicLayoutEffect(() => {
    // Either QA cookie opts into both surfaces, since one experiment now drives both.
    setCookie({
      checked: true,
      enabled: getCookieAsBoolean(ZRM_LISTING_LIST_ENABLED) || getCookieAsBoolean(ZRM_IA_NAV_ENABLED),
    });
  }, []);

  // `!cookie.enabled` keeps QA opt-ins out of the experiment's exposure data.
  const isQueryEnabled = override === undefined && Boolean(loggedIn) && cookie.checked && !cookie.enabled;

  const { data, isPending } = useRentalManagerUserExperimentWebApiHandler_getTreatment(
    { featureName: LISTING_CENTRIC_VIEW_SPLIT_FEATURE_NAME },
    {
      enabled: isQueryEnabled,
      staleTime: Infinity,
      throwOnError: false,
      meta: {
        tags: ['listing-centric-view-treatment'],
        context: { api: 'getTreatment', details: { featureName: LISTING_CENTRIC_VIEW_SPLIT_FEATURE_NAME } },
      },
    },
  );

  if (override !== undefined) {
    return { isEnabled: override, isLoading: false };
  }

  return {
    isEnabled: cookie.enabled || data?.treatment === 'on',
    isLoading: Boolean(loggedIn) && (!cookie.checked || (isQueryEnabled && isPending)),
  };
};

export default useListingCentricViewEnabled;
